// TV board rules (spec, "TV board"): what a board snapshot means for the screen, the chime timing, and what the
// voice says and in which languages. Pure functions, no DOM and no audio: the board page turns the result into a
// flash, a chime and a voice call-out.
import { UI, fill } from './copy';
import type { Lang } from './ticket';
import { MAX_SPOKEN_NUMBER, numberWords } from './spoken';

/** A number that just turned ready flashes this long; the chime plays at the start of the flash. */
export const BOARD_FLASH_MS = 10_000;

/**
 * Number size on the board, as a CSS clamp() over the viewport width: never under the spec's 120 px at 1080p,
 * and about 173 px at 1920 px wide so the digits themselves (roughly 0.7 em) are around 120 px tall.
 */
export const BOARD_NUMBER_SIZE = { minPx: 120, vw: 9, maxPx: 184 } as const;

/** Font size of a board number for a given viewport width, as the clamp() renders it. */
export function boardNumberPx(viewportWidthPx: number, s: { minPx: number; vw: number; maxPx: number } = BOARD_NUMBER_SIZE): number {
  return Math.min(s.maxPx, Math.max(s.minPx, (viewportWidthPx * s.vw) / 100));
}

export interface BoardChime {
  /** Two rising notes. */
  frequenciesHz: readonly [number, number];
  /** Length of one note. */
  noteMs: number;
  /** Start-to-start spacing between the notes. */
  spacingMs: number;
  /** Peak gain of each note, 0 to 1. */
  peakGain: number;
}

export const BOARD_CHIME: BoardChime = { frequenciesHz: [660, 880], noteMs: 500, spacingMs: 350, peakGain: 0.4 };

/** One note of the chime, in seconds from the moment the chime starts. */
export interface ChimeNote {
  frequencyHz: number;
  start: number;
  stop: number;
}

export function chimeSchedule(c: BoardChime = BOARD_CHIME): ChimeNote[] {
  return c.frequenciesHz.map((frequencyHz, i) => ({ frequencyHz, start: (i * c.spacingMs) / 1000, stop: (i * c.spacingMs + c.noteMs) / 1000 }));
}

/** Length of the whole chime; the voice call-out starts once it has finished. */
export function chimeDurationMs(c: BoardChime = BOARD_CHIME): number {
  return (c.frequenciesHz.length - 1) * c.spacingMs + c.noteMs;
}

/** BCP-47 tag the board asks the speech engine for, per interface language. */
export const VOICE_LOCALE: Record<Lang, string> = { fr: 'fr-FR', ar: 'ar-MA', en: 'en-GB' };

/**
 * The languages the board speaks, in order: the venue's default language, then the pickup point's optional second
 * language. A second language equal to the default is spoken once.
 */
export function boardVoiceLangs(defaultLang: Lang, secondLang: Lang | null | undefined): Lang[] {
  return secondLang && secondLang !== defaultLang ? [defaultLang, secondLang] : [defaultLang];
}

/**
 * What the voice says for a number in one language: the whole number ("Numéro quarante-deux", never "quatre deux").
 * Leading zeros are dropped, so a receipt printed 0042 is forty-two. Anything that is not a number in the spoken
 * range (letters, more than six digits) is read character by character, so the voice is never silent on an odd number.
 */
export function boardCallOut(displayNo: string, lang: Lang): string {
  const raw = displayNo.trim();
  const n = /^\d{1,6}$/.test(raw) ? Number(raw) : null;
  const spoken = n !== null && n <= MAX_SPOKEN_NUMBER ? numberWords(n, lang) : raw.split('').join(' ');
  return fill(UI[lang].tv_callout, { order_number: spoken });
}

/** What the board needs to know about a speech engine voice: the shape of the browser's `SpeechSynthesisVoice`. */
export interface VoiceInfo {
  lang: string;
  name?: string;
  default?: boolean;
  localService?: boolean;
}

const voiceTag = (s: string): string => s.trim().toLowerCase().replace(/_/g, '-');

/**
 * The voice to use for a language, or null when the engine has none for it. The exact locale wins (fr-FR), then any
 * voice of that language (fr-CA, ar-SA, a bare "ar"); among equals the engine's default voice, then an offline one,
 * then the first listed. Null means the board chimes without speaking that language: a French voice reading Arabic
 * words would be worse than silence (US-42: chime only when no voice exists).
 */
export function pickVoice<V extends VoiceInfo>(voices: readonly V[], lang: Lang): V | null {
  const want = voiceTag(VOICE_LOCALE[lang]);
  const primary = want.split('-')[0];
  let best: V | null = null;
  let bestScore = -1;
  for (const v of voices) {
    const t = voiceTag(v.lang);
    if (t !== want && t !== primary && !t.startsWith(`${primary}-`)) continue;
    const score = (t === want ? 4 : 0) + (v.default ? 2 : 0) + (v.localService ? 1 : 0);
    if (score > bestScore) {
      best = v;
      bestScore = score;
    }
  }
  return best;
}

/** The languages, in the given order, that the engine can speak. Empty means the chime is the whole alert. */
export function voiceLangsAvailable(voices: readonly VoiceInfo[], langs: readonly Lang[]): Lang[] {
  return langs.filter((l) => pickVoice(voices, l) !== null);
}

export interface BoardAnnouncement<T> {
  /** Ready tickets this screen has not shown before: flash, chime and call them out. Empty when the screen had no memory. */
  fresh: T[];
  /** Every ready number now on screen; hand it back with the next snapshot and keep it for a reload. */
  seen: string[];
}

/**
 * Which ready numbers are new to this screen. `seen` is what the previous call returned, or what a reload restored.
 * `null` means the screen remembers nothing: the whole snapshot becomes the baseline and nothing is announced, so a
 * fresh open (or a reload without memory) with three ready numbers shows them in silence. A number that leaves the
 * ready column and comes back (undo, then ready again) is announced again.
 * Keyed by display number: the public board carries no ticket ids (board-view.ts), and a number is unique among the
 * open tickets of a pickup point.
 */
export function announceReady<T extends { displayNo: string }>(seen: readonly string[] | null, ready: readonly T[]): BoardAnnouncement<T> {
  const nos = ready.map((o) => o.displayNo);
  if (seen === null) return { fresh: [], seen: nos };
  const known = new Set(seen);
  return { fresh: ready.filter((o) => !known.has(o.displayNo)), seen: nos };
}

/**
 * How long the screen's memory of ready numbers survives a reload. Older memory is ignored, so a TV switched on the
 * next morning starts from a silent baseline instead of announcing whatever changed since yesterday.
 */
export const BOARD_MEMORY_MS = 5 * 60_000;

export interface BoardMemory {
  at: number;
  seen: string[];
}

/** Serialise the screen's memory for storage; written on every snapshot and when the page is hidden. */
export function writeBoardMemory(seen: readonly string[], now: number): string {
  return JSON.stringify({ at: now, seen: [...seen] } satisfies BoardMemory);
}

/** Restore the memory written by `writeBoardMemory`: null when there is none, it is unreadable, or it is too old. */
export function readBoardMemory(raw: string | null | undefined, now: number, maxAgeMs: number = BOARD_MEMORY_MS): string[] | null {
  if (!raw) return null;
  try {
    const m = JSON.parse(raw) as Partial<BoardMemory> | null;
    if (!m || typeof m.at !== 'number' || !Array.isArray(m.seen)) return null;
    if (now - m.at > maxAgeMs || m.at - now > maxAgeMs) return null;
    return m.seen.filter((s): s is string => typeof s === 'string');
  } catch {
    return null;
  }
}
